import React, { useEffect, useState } from "react";
import axios from "axios";
import Papa from "papaparse";

import CoronaChart from "components/CoronaChartsNew";
import Layout from "components/Layout";
import Loading from "components/Loading";
import DataTable from "components/DataTable";
import CountySearch from "components/CoronaChartsNew/CountySearch";

import { formatName, numberWithCommas } from "helpers/miscHelpers";
import {
  createTotalsObjectStates,
  processStateDataNew,
  stateSorterJH,
  stateNames,
} from "helpers/CoronaHelpersNew";

import {
  StyledContainer,
  StyledUIGrid,
  StyledAlertBox,
  StyledDivider,
  StyledBorderBox,
} from "styles/pageStyles";

import { StyledSummaryBox } from "components/CoronaChartsNew/styles";

const dataURL_deaths =
  "https://raw.githubusercontent.com/CSSEGISandData/COVID-19/master/csse_covid_19_data/csse_covid_19_time_series/time_series_covid19_deaths_US.csv";
const dataURL_confirmed =
  "https://raw.githubusercontent.com/CSSEGISandData/COVID-19/master/csse_covid_19_data/csse_covid_19_time_series/time_series_covid19_confirmed_US.csv";

// const dataURL_confirmed = "http://localhost:8000/new_usData_confirmed.csv";
// const dataURL_deaths = "http://localhost:8000/new_usData_deaths.csv";

const initialStates = ["tennessee", "", "", ""];

const NewUsData = () => {
  const [countyData, setCountyData] = useState(null);
  const [loadedData, setLoadedData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [theTotals, setTheTotals] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [countyTableData, setCountyTableData] = useState(null);
  const [chartType, setChartType] = useState("newCases");
  // const [noteVisible, setNoteVisible] = useState(true);

  const processCsvData = (data) => {
    setCountyData(data);
    const sortedConfirmed = data.confirmed.sort(stateSorterJH);
    const sortedDeaths = data.deaths.sort(stateSorterJH);

    const dateFields = Object.keys(sortedConfirmed[0]).filter((x) =>
      x.includes("/")
    );

    // put into objects by state
    const stateObjs = stateNames.map((state) => {
      const foundsConfirmed = sortedConfirmed.filter(
        (d) =>
          d.Province_State &&
          d.Province_State.toLowerCase() === state.toLowerCase()
      );
      const foundsDeaths = sortedDeaths.filter(
        (d) =>
          d.Province_State &&
          d.Province_State.toLowerCase() === state.toLowerCase()
      );

      if (foundsConfirmed) {
        const stateObj = {
          name: state,
          timeline: {
            cases: {},
            deaths: {},
          },
        };

        const tempCases = {};
        const tempDeaths = {};

        // for each dateField, combine the numbers for each found location (e.g. all alababma)
        dateFields.forEach((dateField, di) => {
          const totalConfirmedThisDate = foundsConfirmed.reduce((acc, cur) => {
            if (Number(cur[dateField])) {
              return acc + Number(cur[dateField]);
            }

            return acc;
          }, 0);

          const tempCasesObj = {
            [`${dateField}`]: Number(totalConfirmedThisDate),
          };
          Object.assign(tempCases, tempCasesObj);

          const totalDeathsThisDate = foundsDeaths.reduce((acc, cur) => {
            if (Number(cur[dateField])) {
              return acc + Number(cur[dateField]);
            }

            return acc;
          }, 0);

          const tempDeathsObj = {
            [`${dateField}`]: Number(totalDeathsThisDate),
          };

          Object.assign(tempDeaths, tempDeathsObj);
        });

        Object.assign(stateObj.timeline.cases, tempCases);
        Object.assign(stateObj.timeline.deaths, tempDeaths);
        return stateObj;
      }

      return null;
    });

    const newData = processStateDataNew(stateObjs, true);
    // console.log("NewUsData -> newData", newData);
    const totals = createTotalsObjectStates(newData.data);

    const totalsDisplayObj = {
      cases: Object.entries(totals.timeline.cases).slice(-1)[0][1],
      deaths: Object.entries(totals.timeline.deaths).slice(-1)[0][1],
      newCases: Object.entries(totals.timeline.newCases).slice(-1)[0][1],
      newDeaths: Object.entries(totals.timeline.newDeaths).slice(-1)[0][1],
    };
    setTheTotals(totalsDisplayObj);

    newData.data.push(totals);
    setTableData(newData.data);
    setLoadedData(newData.data);

    // console.log("newData:", newData);
  };

  // effects that run on initial load
  useEffect(() => {
    async function dataFetch() {
      const dataStreamConfirmed = await axios.get(dataURL_confirmed);
      const dataConfirmed = dataStreamConfirmed.data;

      const dataStreamDeaths = await axios.get(dataURL_deaths);
      const dataDeaths = dataStreamDeaths.data;

      const parsedConfirmed = Papa.parse(dataConfirmed, { header: true });
      const parsedDeaths = Papa.parse(dataDeaths, { header: true });

      const theData = {
        confirmed: parsedConfirmed.data,
        deaths: parsedDeaths.data,
      };
      // console.log("dataFetch -> theData", theData);

      processCsvData(theData);
    }

    dataFetch();
  }, []);

  useEffect(() => {
    // handle table data change
    function filterTheData(term) {
      if (loadedData) {
        const filteredData = loadedData.filter((x) =>
          term.length > 0 ? x.name.contains(term) : x
        );
        setTableData(filteredData);
      }
    }

    filterTheData(searchTerm);
  }, [loadedData, searchTerm]);

  return (
    <Layout title="COVID-19 (Coronavirus) US Data Graphs">
      <StyledContainer>
        <section>
          <StyledSummaryBox>
            <h3>US Totals:</h3>
            {theTotals.cases && (
              <div className="items">
                <div>
                  Total Cases: <span>{numberWithCommas(theTotals.cases)}</span>
                </div>
                <div>
                  Total Deaths:{" "}
                  <span>{numberWithCommas(theTotals.deaths)}</span>
                </div>
                <div>
                  New Cases: <span>{numberWithCommas(theTotals.newCases)}</span>
                </div>
                <div>
                  New Deaths:{" "}
                  <span>{numberWithCommas(theTotals.newDeaths)}</span>
                </div>
              </div>
            )}
          </StyledSummaryBox>

          <StyledUIGrid className="ui-grid">
            <div className="left-side">
              <StyledBorderBox>
                <h2>
                  Compare By State:{" "}
                  {chartType === "casesPer100k"
                    ? "Total Cases Per 100K Pop."
                    : chartType === "activeCases"
                    ? "Active Cases"
                    : chartType === "newCases"
                    ? "New Daily Cases"
                    : chartType === "newDeaths"
                    ? "New Daily Deaths"
                    : chartType === "deathsPer100k"
                    ? "Deaths Per 100K Pop."
                    : formatName(chartType)}
                </h2>

                {loadedData && (
                  <CoronaChart
                    chartType={chartType}
                    dateCount={10}
                    initialStates={initialStates}
                    loadedData={loadedData}
                    setChartType={setChartType}
                    isUS
                  />
                )}
              </StyledBorderBox>

              <StyledDivider />

              {countyData && (
                <div>
                  <CountySearch data={countyData} />
                </div>
              )}

              {!loadedData && <Loading />}
            </div>

            <div className="right-side">
              <StyledBorderBox>
                <h2 style={{ marginBottom: "10px" }}>State Overview</h2>
                {tableData.length > 0 && <DataTable data={tableData} />}
              </StyledBorderBox>
            </div>
          </StyledUIGrid>
        </section>
      </StyledContainer>
    </Layout>
  );
};

export default NewUsData;
